/**
 * Mice Trail Service
 * REST calls for the Christmas Mice Trail (WordPress mu-plugin: mice-trail.php).
 *
 * Public (QR scan page):
 *   GET  /custom/v1/mice-trail/mouse/{code}
 *   POST /custom/v1/mice-trail/mouse/{code}/claim
 *
 * Admin (Security Centre, needs the MICE_TRAIL_ADMIN_KEY from wp-config.php):
 *   GET    /custom/v1/mice-trail/admin/mice
 *   POST   /custom/v1/mice-trail/admin/generate
 *   POST   /custom/v1/mice-trail/admin/mice/{id}
 *   POST   /custom/v1/mice-trail/admin/mice/{id}/reset
 *   DELETE /custom/v1/mice-trail/admin/mice/{id}
 *   POST   /custom/v1/mice-trail/admin/towns/rename
 */

import config from '../config/environment';

const API_BASE = `${config.apiEndpoint}/custom/v1/mice-trail`;
const KEY_STORAGE = 'miceTrailAdminKey';

export const PUBLIC_SITE_URL = 'https://www.belvoircastle.com';

/** The address printed inside every QR code. */
export const mouseUrl = (code, base = PUBLIC_SITE_URL) => `${base.replace(/\/+$/, '')}/m/${code}`;

export const getAdminKey = () => {
  try { return sessionStorage.getItem(KEY_STORAGE) || ''; } catch { return ''; }
};
export const setAdminKey = (key) => {
  try {
    if (key) sessionStorage.setItem(KEY_STORAGE, key);
    else sessionStorage.removeItem(KEY_STORAGE);
  } catch { /* storage unavailable */ }
};

async function request(path, { method = 'GET', body, admin = false } = {}) {
  const headers = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (admin) headers['X-Mice-Admin-Key'] = getAdminKey();

  let response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      cache: 'no-store',
    });
  } catch {
    const err = new Error('We could not reach the server. Please check your signal and try again.');
    err.network = true;
    throw err;
  }

  const text = await response.text();
  let json = null;
  try { json = JSON.parse(text); } catch { /* handled below */ }

  if (!response.ok) {
    const err = new Error(json?.message || `Something went wrong (${response.status}).`);
    err.status = response.status;
    err.code = json?.code;
    err.data = json?.data?.data || null; // e.g. details of a mouse that was already found
    throw err;
  }
  if (!json) throw new Error('The server sent an unexpected reply.');
  return json;
}

// ---- Public ----------------------------------------------------------------

export async function getMouse(code) {
  const json = await request(`/mouse/${encodeURIComponent(code)}`);
  return json.data;
}

export async function claimMouse(code, details) {
  const json = await request(`/mouse/${encodeURIComponent(code)}/claim`, { method: 'POST', body: details });
  return json.data;
}

// ---- Admin -----------------------------------------------------------------

export async function adminListMice() {
  const json = await request('/admin/mice', { admin: true });
  return json.data || [];
}

export async function adminGenerateMice(town, count) {
  const json = await request('/admin/generate', { method: 'POST', admin: true, body: { town, count } });
  return json.data;
}

export async function adminUpdateMouse(id, fields) {
  const json = await request(`/admin/mice/${id}`, { method: 'POST', admin: true, body: fields });
  return json.data;
}

export async function adminResetMouse(id) {
  const json = await request(`/admin/mice/${id}/reset`, { method: 'POST', admin: true });
  return json.data;
}

export async function adminDeleteMouse(id) {
  await request(`/admin/mice/${id}`, { method: 'DELETE', admin: true });
}

export async function adminRenameTown(from, to) {
  await request('/admin/towns/rename', { method: 'POST', admin: true, body: { from, to } });
}
